import { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Button, EditableTitle, Tooltip } from 'src/components/atoms';
import {
  StyledIcon,
  StyledIconInButton,
} from 'src/components/foundations/foundations.styles';
import { casePageTitleBar } from 'src/components/pages/Case/Case.slice';
import {
  CaseByIdQuery,
  TagType,
  useAddTagToCaseMutation,
  useRemoveTagFromCaseMutation,
  useUpdateCaseDescriptionMutation,
  useUpdateCaseTitleMutation,
} from 'src/graphql';
import { AppDispatch } from 'src/store';
import { handleErrorToast, handleSuccessToast } from '../Toast';

import { MultiValue, SingleValue } from 'react-select';
import { CaseSummary } from 'src/components/organisms';
import { caseTagUpdated } from 'src/components/pages/Case/actions/caseTagUpdated';
import { CaseTagsWithOverflow } from 'src/components/pages/Cases/components';
import { CaseTypeEnum, FormInputVariant, FormState } from 'src/types';
import { CaseScreenBackButton } from '../CaseScreenBackButton';
import { DefaultOption } from '../DropdownSelect';
import { RightCheckboxOption } from '../DropdownSelect/components/RightCheckboxOption';
import {
  TagsGroupBase,
  TagsGroupHeading,
} from '../DropdownSelect/components/TagsGroupHeading';
import { FormInput } from '../FormInput';
import {
  CaseTagsWrapper,
  LeftSideWrapper,
  TitleContainer,
} from './CaseScreenTitle.styles';
import { getGroupedTags } from './helpers/getGroupedTags';

export interface CaseScreenTitleProps {
  caseType: CaseTypeEnum;
  onInitiateInvestigation?: () => void;
  isInvestigationActive?: boolean;
}

export const CaseScreenTitle = ({
  caseType,
  onInitiateInvestigation,
  isInvestigationActive = false,
}: CaseScreenTitleProps) => {
  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();
  const dispatch = useDispatch<AppDispatch>();
  const {
    caseId = '',
    caseTitle = '',
    permissions,
    caseTags = [] as TagType[],
    organisationTags = [],
    description,
    descriptionLastUpdated,
  } = useSelector(casePageTitleBar);
  const [newCaseTitle, setNewCaseTitle] = useState<string>(caseTitle || '');

  useEffect(() => {
    setNewCaseTitle(caseTitle || '');
  }, [caseTitle]);
  const [toggleSummaryTooltip, setToggleSummaryTooltip] = useState(false);
  const [toggleSummaryTooltipRoot, setToggleSummaryTooltipRoot] =
    useState<HTMLElement | null>(null);
  const [caseSummaryIsOpen, setCaseSummaryIsOpen] = useState(false);

  const addCaseTag = useAddTagToCaseMutation();
  const removeCaseTag = useRemoveTagFromCaseMutation();

  const updateCaseTitle = useUpdateCaseTitleMutation({
    onMutate: async (variables) => {
      queryClient.cancelQueries(['CaseById', { id: variables.caseId }]);
      const existingData =
        queryClient.getQueryData<CaseByIdQuery>([
          'CaseById',
          { id: variables.caseId },
        ]) ?? {};

      queryClient.setQueryData(['CaseById', { id: variables.caseId }], {
        caseById: {
          ...existingData.caseById,
          name: variables.newCaseTitle,
        },
      });

      return {
        previousCaseData: existingData.caseById,
      };
    },
    onError: (_, variables, context) => {
      queryClient.setQueryData(['CaseById', { id: variables.caseId }], {
        caseById: context?.previousCaseData,
      });

      handleErrorToast({
        toastMessage: 'Failed to update case title',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries(['CaseById', { id: caseId }]);
    },
  });

  const updateCaseSummary = useUpdateCaseDescriptionMutation({
    onMutate: async (variables) => {
      queryClient.cancelQueries(['CaseById', { id: variables.caseId }]);
      const existingData =
        queryClient.getQueryData<CaseByIdQuery>([
          'CaseById',
          { id: variables.caseId },
        ]) ?? {};

      queryClient.setQueryData(['CaseById', { id: variables.caseId }], {
        caseById: {
          ...existingData.caseById,
          description: variables.description,
          descriptionUpdatedAt: new Date().toISOString(),
        },
      });

      handleSuccessToast({
        toastMessage: 'Case summary updated',
      });

      return {
        previousCaseData: existingData.caseById,
      };
    },
    onError: (_, variables, context) => {
      queryClient.setQueryData(['CaseById', { id: variables.caseId }], {
        caseById: context?.previousCaseData,
      });

      handleErrorToast({
        toastMessage: 'Failed to update case summary',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries(['CaseById', { id: caseId }]);
    },
  });

  const handleBackButtonClick = () => {
    const fallbackRoute =
      caseType === CaseTypeEnum.COI ? '/disclosures' : '/cases';
    location.key ? history.goBack() : history.push(fallbackRoute);
  };

  const handleUpdateCaseTitle = () => {
    updateCaseTitle.mutate({
      newCaseTitle,
      caseId,
    });
  };

  const handleCaseTagOnChange = (
    selectedOptions: SingleValue<DefaultOption> | MultiValue<DefaultOption>,
  ) => {
    dispatch(
      caseTagUpdated({
        caseId,
        selectedTagOptions: selectedOptions,
        mutationAddCaseTag: addCaseTag.mutateAsync,
        mutationRemoveCaseTag: removeCaseTag.mutateAsync,
        queryClient,
      }),
    );
  };
  const handleRemoveTag = (tagId: string) => {
    dispatch(
      caseTagUpdated({
        caseId,
        selectedTagOptions: caseTags
          .filter((tag) => tag.id !== tagId)
          .map((tag) => ({
            value: tag.id,
            label: tag.name,
          })),
        mutationAddCaseTag: addCaseTag.mutateAsync,
        mutationRemoveCaseTag: removeCaseTag.mutateAsync,
        queryClient,
      }),
    );
  };

  const handleUpdateCaseSummary = (description: string) => {
    updateCaseSummary.mutate({
      description: description === '<p><br></p>' ? null : description,
      caseId,
    });
  };

  const hasCanEditPermissions = Boolean(
    caseType === CaseTypeEnum.SPEAK_UP && permissions?.canEditCaseDetails,
  );
  const showTags =
    organisationTags.length > 0 && permissions?.canViewCaseDetails;

  const { groupedOptions, selectedOptions } = getGroupedTags(
    organisationTags,
    caseTags,
  );

  const canSeeSummaryTooltip =
    (permissions?.canViewCaseDetails && Boolean(description)) ||
    permissions?.canEditCaseDetails;

  const handleSummaryClick = () => {
    canSeeSummaryTooltip && setCaseSummaryIsOpen(!caseSummaryIsOpen);
  };

  return (
    <TitleContainer>
      <LeftSideWrapper>
        <CaseScreenBackButton
          onClick={handleBackButtonClick}
          data-testid="case-back-button"
        />
        <EditableTitle
          value={newCaseTitle}
          setValue={setNewCaseTitle}
          defaultValue="Add case name"
          disabled={
            caseType === CaseTypeEnum.COI || !permissions?.canEditCaseDetails
          }
          onSave={handleUpdateCaseTitle}
          data-testid="case-title-text-input"
          fixedHeight
        />
        {caseType === CaseTypeEnum.SPEAK_UP && (
          <div
            ref={setToggleSummaryTooltipRoot}
            onMouseOver={() => setToggleSummaryTooltip(true)}
            onMouseOut={() => setToggleSummaryTooltip(false)}
            onClick={handleSummaryClick}
          >
            <Button
              variant="secondary"
              size="small"
              width="28px"
              noMinWidth={true}
              disabled={!canSeeSummaryTooltip}
              data-testid="case-summary-button"
            >
              <StyledIcon className="uil uil-subject" size="medium" />
            </Button>
            {toggleSummaryTooltip && canSeeSummaryTooltip && (
              <Tooltip
                text={'Case summary'}
                variant="up"
                root={toggleSummaryTooltipRoot as HTMLElement}
              />
            )}
          </div>
        )}
      </LeftSideWrapper>
      {showTags && (
        <CaseTagsWrapper>
          <CaseTagsWithOverflow
            tags={caseTags}
            readOnly={!hasCanEditPermissions}
            onTagClick={handleRemoveTag}
            tagsAlignment="right"
          />
          <FormInput<DefaultOption, TagsGroupBase>
            variant={FormInputVariant.DROPDOWN}
            options={groupedOptions}
            value={selectedOptions}
            buttonText="Add tag"
            buttonIcon="uil uil-plus"
            buttonVariant="secondary"
            highlightButtonWhenOpen={true}
            isMulti={true}
            hideDropdownIndicator={true}
            controlShouldRenderValue={false}
            components={{
              GroupHeading: TagsGroupHeading,
              Option: RightCheckboxOption,
            }}
            onChange={handleCaseTagOnChange}
            menuPosition="absolute"
            menuPlacement="auto"
            alignRight={true}
            width="300px"
            state={
              caseType === CaseTypeEnum.COI || !permissions?.canEditCaseDetails
                ? FormState.DISABLED
                : FormState.DEFAULT
            }
            $containerWidth="fit-content"
            data-testid="add-tag-dropdown"
          />
          {caseType === CaseTypeEnum.SPEAK_UP &&
            onInitiateInvestigation &&
            permissions?.canEditCaseWorkspaceTab &&
            !isInvestigationActive && (
              <Button
                variant="primary"
                size="medium"
                noTextWrap
                data-testid="case-screen-title-initiate-investigation-button"
                onClick={onInitiateInvestigation}
              >
                <StyledIconInButton
                  size="small"
                  className="uil uil-clipboard-notes"
                  position="left"
                />
                Initiate Investigation
              </Button>
            )}
        </CaseTagsWrapper>
      )}
      {caseType === CaseTypeEnum.SPEAK_UP && (
        <CaseSummary
          isOpen={caseSummaryIsOpen}
          closeSummary={() => {
            setCaseSummaryIsOpen(false);
          }}
          saveSummary={(description: string) =>
            handleUpdateCaseSummary(description)
          }
          descriptionLastUpdated={descriptionLastUpdated}
          description={description}
          hasCanEditPermissions={hasCanEditPermissions}
        />
      )}
    </TitleContainer>
  );
};
